'use client';

import dynamic from 'next/dynamic';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/app/components/button';
import { Container } from '@/app/components/layouts/container';
import { type TabItem, Tabs } from '@/app/components/navigation/tabs';
import { BaseDealerPanel } from '@/app/components/shared/dealer/components/shared/base-dealer-panel';
import { MobileDrawer } from '@/app/components/shared/dealer/components/shared/mobile-drawer';
import { AddressBox } from '@/app/components/shared/dealer/components/shared/search-panel/dealer-detail/address-box';
import { DealerDetailHeader } from '@/app/components/shared/dealer/components/shared/search-panel/dealer-detail/header';
import { ServiceDetail } from '@/app/components/shared/dealer/components/shared/search-panel/dealer-detail/service-detail';
import {
  DEALER_MY_ISUZU_APPOINTMENT,
  DEALER_SEARCH_KEY,
} from '@/app/components/shared/dealer/utils/dealer-constant';
import {
  getDealerGoogleMapUrl,
  isDealerActiveAfterSale,
  isDealerActiveBodyPaint,
  isDealerActiveSale,
  isDealerCV,
  isDealerRegisterable,
} from '@/app/components/shared/dealer/utils/mapping-dealer';
import { EVENT_NAME } from '@/app/components/shared/dealer/utils/tracking';
import type { DealerSearch } from '@/app/components/shared/dealer/utils/types';
import { useDisclosure } from '@/app/hooks/useDisclosure';
import { createDataAttributes } from '@/app/utils/createDataAttributes';
import { createDataGA } from '@/app/utils/createDataGA';

const ModalRegister = dynamic(
  () =>
    import(
      '@/app/components/shared/dealer/components/shared/modal-register'
    ).then((mod) => mod.ModalRegister),
  {
    ssr: false,
  }
);

const TAB_DEALER_TYPES = [
  {
    value: DEALER_SEARCH_KEY.Showroom,
    label: 'โชว์รูม',
  },
  {
    value: DEALER_SEARCH_KEY.Service,
    label: 'ศูนย์บริการ',
  },
  {
    value: DEALER_SEARCH_KEY.BodyPaint,
    label: 'ศูนย์ตัวถัง / สี',
  },
];

const TAB_DEALER_DETAIL = [
  {
    value: 'lcv',
    label: 'รถปิกอัพ/อเนกประสงค์',
  },
  {
    value: 'cv',
    label: 'รถบรรทุก',
  },
];

interface SearchPanelDealerDetailProps {
  dealer: DealerSearch;
  activeTab?: string;
  activeDetailTab?: string;
  onClickCurrentLocation: () => void;
  onClose: (isDetail?: boolean) => void;
}

export const SearchPanelDealerDetail = ({
  dealer,
  activeTab,
  activeDetailTab,
  onClose,
  onClickCurrentLocation,
}: SearchPanelDealerDetailProps) => {
  const [isOpen, { open: openModalRegister, close: closeModalRegister }] =
    useDisclosure(false);

  const [selectedTab, setSelectedTab] = useState<TabItem | null>(null);
  const [selectedDetailTab, setSelectedDetailTab] = useState<TabItem | null>(
    null
  );

  const handleChangeTab = (item: TabItem) => {
    setSelectedTab(item);
    setSelectedDetailTab(
      isDealerCV ? TAB_DEALER_DETAIL[1] : TAB_DEALER_DETAIL[0]
    );
  };

  const selectedType = useMemo(() => {
    switch (selectedTab?.value) {
      case DEALER_SEARCH_KEY.Showroom:
        return dealer.sale;
      case DEALER_SEARCH_KEY.Service:
        return dealer.aftersale;
      case DEALER_SEARCH_KEY.BodyPaint:
        return dealer.bp;
      default:
        return null;
    }
  }, [dealer, selectedTab]);

  const handleChangeDetailTab = (item: TabItem) => {
    setSelectedDetailTab(item);
  };

  const tabs = useMemo(() => {
    const items: TabItem[] = [];

    if (isDealerActiveSale(dealer)) {
      items.push(TAB_DEALER_TYPES[0]);
    }

    if (isDealerActiveAfterSale(dealer)) {
      items.push(TAB_DEALER_TYPES[1]);
    }

    if (isDealerActiveBodyPaint(dealer)) {
      items.push(TAB_DEALER_TYPES[2]);
    }

    return items;
  }, [dealer]);

  const tabDetails = useMemo(() => {
    const items: TabItem[] = [];

    if (selectedType?.active_lcv) {
      items.push(TAB_DEALER_DETAIL[0]);
    }

    if (selectedType?.active_cv) {
      items.push(TAB_DEALER_DETAIL[1]);
    }

    return items;
  }, [selectedType]);

  useEffect(() => {
    if (activeTab) {
      const tab = tabs.find((item) => item.value === activeTab);
      return handleChangeTab(tab as TabItem);
    }
    handleChangeTab(tabs[0]);
  }, [tabs, activeTab]);

  useEffect(() => {
    if (activeDetailTab) {
      const tab = TAB_DEALER_DETAIL.find(
        (item) => item.value === activeDetailTab
      );
      return handleChangeDetailTab(tab as TabItem);
    }
    handleChangeDetailTab(TAB_DEALER_DETAIL[0]);
  }, [activeDetailTab]);

  const renderFooter = () => {
    const dataTest = 'footer-cta-button';
    const baseDataGA = {
      click: true,
      eventName: EVENT_NAME,
      group:
        'components/shared/dealer/components/shared/search-panel/dealer-detail',
    };

    if (
      selectedTab?.value === DEALER_SEARCH_KEY.Showroom &&
      process.env.NEXT_PUBLIC_WEB === selectedDetailTab?.value &&
      isDealerRegisterable(dealer)
    ) {
      return (
        <Button
          width="full"
          onClick={openModalRegister}
          {...createDataAttributes({
            test: dataTest,
          })}
          {...createDataGA({
            ...baseDataGA,
            value: dealer.uid,
            text: 'ลงทะเบียนสนใจรถ / ทดลองขับ',
          })}
        >
          ลงทะเบียนสนใจรถ / ทดลองขับ
        </Button>
      );
    }

    if (
      selectedTab?.value === DEALER_SEARCH_KEY.Service ||
      selectedTab?.value === DEALER_SEARCH_KEY.BodyPaint
    ) {
      return (
        <Button
          width="full"
          href={DEALER_MY_ISUZU_APPOINTMENT}
          target="_blank"
          {...createDataAttributes({
            test: dataTest,
          })}
          {...createDataGA({
            ...baseDataGA,
            value: dealer.uid,
            text: 'นัดหมายผ่าน my-ISUZU App',
            url: DEALER_MY_ISUZU_APPOINTMENT,
          })}
        >
          นัดหมายผ่าน my-ISUZU App
        </Button>
      );
    }

    return null;
  };

  const googleMapUrl = useMemo(() => {
    return getDealerGoogleMapUrl(dealer, selectedType?.name_th || '');
  }, [dealer, selectedType]);

  const handleOnClose = () => {
    onClose(true);
  };

  const renderContent = () => {
    return (
      <>
        <AddressBox selectedType={selectedType} googleMapUrl={googleMapUrl} />

        <div
          className="bg-silver-50 mb-16 min-h-fit rounded-xs px-4 pb-16 xl:mb-0 xl:h-full xl:pb-3"
          {...createDataAttributes({ test: 'detail-box' })}
        >
          <Tabs
            variant="underline"
            wrapperClassName="w-full"
            className="text-body-2-fixed w-full max-w-full overflow-x-auto px-1"
            items={tabDetails}
            value={selectedDetailTab?.value || ''}
            onChange={(value, index, item) => handleChangeDetailTab(item)}
            forceHide={false}
          />
          <ServiceDetail
            selectedTab={selectedTab}
            selectedDetailTab={selectedDetailTab || TAB_DEALER_DETAIL[0]}
            selectedType={selectedType}
            dealer={dealer}
          />
        </div>
      </>
    );
  };

  const renderHeader = () => {
    return (
      <DealerDetailHeader
        selectedTab={selectedTab}
        selectedType={selectedType}
        tabs={tabs}
        handleChangeTab={handleChangeTab}
        onClose={handleOnClose}
      />
    );
  };

  return (
    <>
      <BaseDealerPanel
        contentClassName="px-4 hidden flex-col space-y-0 gap-y-3 xl:flex"
        header={renderHeader()}
        footer={renderFooter()}
      >
        {renderContent()}
      </BaseDealerPanel>

      <MobileDrawer
        header={
          <Container
            className="shrink-0 space-y-3 pb-3"
            size="mobile-only"
            {...createDataAttributes({ test: 'drawer-header' })}
          >
            {renderHeader()}
          </Container>
        }
        footer={renderFooter()}
        onClickCurrentLocation={onClickCurrentLocation}
        dealerDetail
        isShowBackButton={!!dealer}
        onClickBackButton={handleOnClose}
      >
        <Container
          className="space-y-3"
          size="mobile-only"
          {...createDataAttributes({ test: 'drawer-content' })}
        >
          {renderContent()}
        </Container>
      </MobileDrawer>

      {!!isOpen && (
        <ModalRegister
          isOpen={isOpen}
          onClose={closeModalRegister}
          data={{
            ...dealer,
            ...selectedType,
          }}
        />
      )}
    </>
  );
};
